import React, { useMemo, useState } from "react";
import { Search, RotateCcw } from "lucide-react";
import { fmtNum, positionColor } from "@/lib/format";

const TIERS = ["S", "A", "B", "C", "D", "F"];
const POSITIONS = ["ALL", "QB", "RB", "WR", "TE", "ROOKIE"];

function headshotUrl(player) {
  const id = player.sleeperId || player.id;
  if (!id || String(id).startsWith("DP_") || String(id).startsWith("FP_")) return null;
  return `https://sleepercdn.com/content/nfl/players/${id}.jpg`;
}

function playerKey(player) {
  return String(player.sleeperId || player.id || player.name);
}

function isRookie(player) {
  return Number(player.yearsExperience) === 0;
}

export function TierListMaker({ players }) {
  const [tiers, setTiers] = useState(() => Object.fromEntries(TIERS.map((tier) => [tier, []])));
  const [q, setQ] = useState("");
  const [pos, setPos] = useState("ALL");

  const rankedPlayers = useMemo(() => (
    [...players]
      .filter((p) => p.position && p.position !== "PICK")
      .sort((a, b) => (a.combinedRank || 9999) - (b.combinedRank || 9999))
      .slice(0, 200)
      .map((p, index) => ({ ...p, rotoTradeRank: index + 1 }))
  ), [players]);

  const playerByKey = useMemo(() => {
    const map = new Map();
    for (const player of rankedPlayers) map.set(playerKey(player), player);
    return map;
  }, [rankedPlayers]);

  const assignedKeys = useMemo(() => new Set(Object.values(tiers).flat()), [tiers]);

  const poolPlayers = useMemo(() => {
    let rows = rankedPlayers.filter((player) => !assignedKeys.has(playerKey(player)));
    if (pos === "ROOKIE") rows = rows.filter(isRookie);
    else if (pos !== "ALL") rows = rows.filter((player) => player.position === pos);
    if (q.trim()) {
      const term = q.trim().toLowerCase();
      rows = rows.filter((player) => (
        player.name.toLowerCase().includes(term) ||
        (player.team || "").toLowerCase().includes(term)
      ));
    }
    return rows;
  }, [assignedKeys, pos, q, rankedPlayers]);

  const tierRanks = useMemo(() => {
    const map = new Map();
    let rank = 1;
    for (const tier of TIERS) {
      for (const key of tiers[tier]) {
        map.set(key, rank++);
      }
    }
    return map;
  }, [tiers]);

  const startDrag = (event, payload) => {
    event.dataTransfer.effectAllowed = "move";
    event.dataTransfer.setData("application/json", JSON.stringify(payload));
  };

  const dropIntoTier = (event, targetTier, targetIndex = null) => {
    event.preventDefault();
    const raw = event.dataTransfer.getData("application/json");
    if (!raw) return;
    const payload = JSON.parse(raw);
    const key = payload.key;
    if (!playerByKey.has(key)) return;

    setTiers((prev) => {
      const next = Object.fromEntries(TIERS.map((tier) => [tier, prev[tier].filter((item) => item !== key)]));
      const insertAt = targetIndex === null ? next[targetTier].length : targetIndex;
      next[targetTier] = [
        ...next[targetTier].slice(0, insertAt),
        key,
        ...next[targetTier].slice(insertAt),
      ];
      return next;
    });
  };

  const reset = () => setTiers(Object.fromEntries(TIERS.map((tier) => [tier, []])));

  return (
    <section className="relative" data-testid="tier-list-maker">
      <div className="rounded-none border border-[#24242A] bg-[#0B0B0F] text-white shadow-[8px_8px_0_0_#000]">
        <div className="border-b border-[#24242A] px-4 py-4 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <span className="text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA] font-mono-data">02 / Tier List Maker</span>
            <h2 className="font-display font-black text-3xl sm:text-4xl tracking-tighter mt-1">
              Dynasty <span className="text-[#A78BFA]">tiers</span>.
            </h2>
            <p className="text-sm text-[#A1A1AA] mt-2 max-w-2xl">
              Top 200 dynasty players ranked locally from the RotoTrade-style board and matched to Sleeper headshots.
            </p>
          </div>
          <button
            onClick={reset}
            className="border border-[#A1A1AA]/40 px-4 py-2 text-xs uppercase tracking-widest hover:bg-white hover:text-[#0B0B0F] transition-colors inline-flex items-center gap-2"
            data-testid="tier-reset"
          >
            <RotateCcw size={13} /> Reset Board
          </button>
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[1fr_380px] min-h-[760px]">
          <div className="p-4 lg:p-5 space-y-3 bg-[radial-gradient(circle_at_top_left,#1E1B4B_0,#0B0B0F_35%,#050507_100%)]">
            {TIERS.map((tier) => (
              <TierRow
                key={tier}
                tier={tier}
                playerKeys={tiers[tier]}
                playerByKey={playerByKey}
                tierRanks={tierRanks}
                onDragStart={startDrag}
                onDrop={dropIntoTier}
              />
            ))}
            <div className="text-xs text-[#A1A1AA] font-mono-data px-2 pt-2">
              Drag players from the right pool into tiers. Ranks update automatically from S through F.
            </div>
          </div>

          <aside className="border-t xl:border-t-0 xl:border-l border-[#24242A] bg-[#111116]" data-testid="tier-player-pool">
            <div className="sticky top-[92px] bg-[#111116] z-20 border-b border-[#24242A] p-3 space-y-3">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] text-[#A1A1AA] font-mono-data">Player Pool</div>
                  <div className="font-display font-black text-xl tracking-tight">{poolPlayers.length} available</div>
                </div>
                <div className="text-right font-mono-data text-[10px] text-[#A1A1AA]">
                  Top 200<br />Dynasty
                </div>
              </div>
              <div className="relative">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#A1A1AA]" />
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Search player or team..."
                  className="w-full bg-[#050507] border border-[#3F3F46] focus:border-[#A78BFA] outline-none pl-9 pr-3 py-2.5 text-sm text-white placeholder:text-[#71717A] font-mono-data"
                  data-testid="tier-search"
                />
              </div>
              <div className="flex flex-wrap gap-1" data-testid="tier-position-filters">
                {POSITIONS.map((position) => (
                  <button
                    key={position}
                    onClick={() => setPos(position)}
                    className={`px-2.5 py-1.5 text-xs uppercase tracking-widest border transition-colors ${pos === position ? "bg-white text-[#0B0B0F] border-white" : "bg-[#050507] text-white border-[#3F3F46] hover:border-white"}`}
                    data-testid={`tier-pos-${position}`}
                  >
                    {position}
                  </button>
                ))}
              </div>
            </div>

            <div className="p-3 space-y-3 max-h-[680px] overflow-auto">
              {poolPlayers.map((player) => (
                <PlayerCard
                  key={playerKey(player)}
                  player={player}
                  rank={player.rotoTradeRank}
                  draggable
                  onDragStart={(event) => startDrag(event, { source: "pool", key: playerKey(player) })}
                />
              ))}
              {poolPlayers.length === 0 && (
                <div className="border border-dashed border-[#3F3F46] text-center py-10 text-sm text-[#A1A1AA]">
                  No players match.
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
}

function TierRow({ tier, playerKeys, playerByKey, tierRanks, onDragStart, onDrop }) {
  const tierColors = {
    S: "bg-[#A78BFA] text-[#0B0B0F]",
    A: "bg-[#7C3AED] text-white",
    B: "bg-[#2563EB] text-white",
    C: "bg-[#0891B2] text-white",
    D: "bg-[#52525B] text-white",
    F: "bg-[#18181B] text-white",
  };

  return (
    <div className="grid grid-cols-[64px_1fr] border border-[#2A2A31] bg-[#0F0F14]/90 min-h-[116px]" data-testid={`tier-${tier}`}>
      <div className={`flex items-center justify-center font-display font-black text-4xl border-r border-[#2A2A31] ${tierColors[tier]}`}>
        {tier}
      </div>
      <div
        onDragOver={(event) => event.preventDefault()}
        onDrop={(event) => onDrop(event, tier)}
        className="p-3 flex flex-wrap gap-3 content-start min-h-[116px]"
      >
        {playerKeys.length === 0 && (
          <div className="self-center text-sm text-[#71717A] font-mono-data">Drag players here</div>
        )}
        {playerKeys.map((key, index) => {
          const player = playerByKey.get(key);
          if (!player) return null;
          return (
            <div
              key={key}
              onDragOver={(event) => event.preventDefault()}
              onDrop={(event) => onDrop(event, tier, index)}
            >
              <PlayerCard
                player={player}
                rank={tierRanks.get(key)}
                compact
                draggable
                onDragStart={(event) => onDragStart(event, { source: "tier", tier, key })}
              />
            </div>
          );
        })}
      </div>
    </div>
  );
}

function PlayerCard({ player, rank, compact = false, draggable = false, onDragStart }) {
  const image = headshotUrl(player);
  const sizeClass = compact ? "w-[172px]" : "w-full";
  const imgClass = compact ? "w-14 h-14" : "w-20 h-20";

  return (
    <div
      draggable={draggable}
      onDragStart={onDragStart}
      className={`${sizeClass} cursor-grab active:cursor-grabbing select-none border border-[#2A2A31] bg-[#18181B] hover:border-[#A78BFA] transition-colors shadow-[3px_3px_0_0_#000]`}
      data-testid={`tier-card-${playerKey(player)}`}
    >
      <div className="p-3 flex gap-3">
        <div className={`${imgClass} bg-[#050507] border border-[#3F3F46] overflow-hidden flex items-center justify-center shrink-0`}>
          {image ? (
            <img
              src={image}
              alt={player.name}
              className="w-full h-full object-cover"
              onError={(event) => { event.currentTarget.style.display = "none"; }}
            />
          ) : (
            <span className="text-xs text-[#A1A1AA] font-mono-data">{player.position}</span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span className="text-[10px] font-mono-data text-[#A1A1AA] tabular-nums">#{rank || player.rotoTradeRank}</span>
            <span className={`text-[9px] font-bold px-1.5 py-0.5 ${positionColor(player.position)}`}>{player.position}</span>
          </div>
          <div className="font-display font-bold text-base leading-tight truncate mt-1 text-white">{player.name}</div>
          <div className="text-[11px] text-[#A1A1AA] font-mono-data mt-1 truncate">
            {player.team || "FA"} · {fmtNum(player.combinedValue)}
          </div>
        </div>
      </div>
    </div>
  );
}
